import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  X,
  Calculator,
  Wrench,
  Users,
  MessageSquare,
  Zap,
  ShieldCheck,
  Building2,
  BadgeCheck,
  FileText,
  Sparkles,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Mail,
  Phone,
  MapPin,
} from "lucide-react";
import { useBooking } from "../BookingContext";
import { useT } from "../i18n";
import SiteFooter from "../components/SiteFooter";

/**
 * PropHero — TECH-FORWARD REDESIGN MOCK-UP
 * ------------------------------------------------------------------
 * A standalone preview route at /preview.
 * Does NOT affect the production landing page at /.
 * Uses Outfit + IBM Plex Sans + JetBrains Mono (loaded in index.html).
 * ------------------------------------------------------------------
 */

const FONT_HEAD = { fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro", "Helvetica Neue", Arial, sans-serif' };
const FONT_BODY = { fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro", "Helvetica Neue", Arial, sans-serif' };
const FONT_MONO = { fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro", "Helvetica Neue", Arial, sans-serif', fontWeight: 600 };

const BLUE = "#023CFA";
const BLUE_HOVER = "#0131CD";
const TEXT = "#0A0A0A";
const MUTED = "#4B5563";
const BORDER = "#E5E7EB";
const SURFACE = "#F9FAFB";

export default function PreviewMock() {
  const { open: openBooking } = useBooking();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div
      data-testid="preview-mock"
      className="min-h-screen bg-white text-[#0A0A0A]"
      style={FONT_BODY}
    >
      <Header openBooking={openBooking} />
      <Hero openBooking={openBooking} />
      <ProofStrip />
      <ServicesBento />
      <TechnologyPortal />
      <Comparison />
      <OnboardingStepper />
      <FinalCta openBooking={openBooking} />
      <SiteFooter />
    </div>
  );
}

/* -------------------- Preview banner -------------------- */
function PreviewBanner() {
  return (
    <div
      className="w-full bg-[#0A0A0A] text-white text-center py-2 px-4"
      style={FONT_MONO}
      data-testid="preview-banner"
    >
      <span className="text-[11px] tracking-[0.15em] uppercase">
        Mock preview — not live · <Link to="/" className="underline underline-offset-4 hover:text-[#023CFA]">← Back to current site</Link>
      </span>
    </div>
  );
}

/* -------------------- Header -------------------- */
function Header({ openBooking }) {
  const { lang, setLang, t } = useT();
  const navItems = [
    { key: "services",   label: t("pm.nav.services") },
    { key: "technology", label: t("pm.nav.technology") },
    { key: "compare",    label: t("pm.nav.compare") },
    { key: "process",    label: t("pm.nav.process") },
  ];
  return (
    <header
      className="sticky top-0 z-40 border-b"
      style={{
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        background: "rgba(255,255,255,0.72)",
        borderColor: `${BORDER}66`,
      }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-2.5 flex items-center justify-between">
        <a href="#top" className="flex items-center" aria-label="PropHero">
          <img src="/prophero-logo.png" alt="PropHero" className="h-5 w-auto" />
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {navItems.map((n) => (
            <a
              key={n.key}
              href={`#${n.key}`}
              className="text-[12px] font-normal text-[#1D1D1F]/85 hover:text-[#0A0A0A] transition-colors"
              style={{ ...FONT_BODY, letterSpacing: "-0.01em" }}
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setLang(lang === "en" ? "es" : "en")}
            data-testid="preview-header-lang-toggle"
            aria-label={lang === "en" ? "Switch to Spanish" : "Switch to English"}
            className="inline-flex items-center justify-center min-w-[36px] px-2.5 py-1 rounded-full text-[11px] font-normal text-[#515154] border transition-colors hover:text-[#0A0A0A]"
            style={{ borderColor: BORDER, ...FONT_BODY, letterSpacing: "0.02em" }}
          >
            {lang === "en" ? "EN" : "ES"}
          </button>
          <button
            onClick={() => openBooking("preview_header_cta", "Schedule Consultation")}
            data-testid="preview-header-cta"
            className="inline-flex items-center bg-[#023CFA] hover:bg-[#1E52FF] text-white px-3.5 py-1.5 rounded-full text-[12px] font-normal transition-colors"
            style={{ ...FONT_BODY, letterSpacing: "-0.01em" }}
          >
            {t("pm.cta.schedule")}
          </button>
        </div>
      </div>
    </header>
  );
}

/* -------------------- Hero -------------------- */
function Hero({ openBooking }) {
  const { t } = useT();
  return (
    <section id="top" className="border-b" style={{ borderColor: BORDER }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 grid lg:grid-cols-12 gap-12 items-center">
        {/* Left copy */}
        <div className="lg:col-span-7">
          <h1
            className="text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight font-semibold"
            style={{ ...FONT_HEAD, color: "#0A1428", letterSpacing: "-0.02em" }}
          >
            {t("pm.hero.h1a")}<br />{t("pm.hero.h1b")}<br />
            <span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage:
                  "linear-gradient(90deg, #023CFA 0%, #3A6BFF 50%, #6B8FFF 100%)",
              }}
            >
              {t("pm.hero.h1c")}
            </span>
          </h1>
          <p className="mt-6 max-w-lg text-[16px] leading-relaxed text-[#4B5563]">
            {t("pm.hero.sub")}
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <button
              onClick={() => openBooking("preview_hero_primary", "Schedule Consultation")}
              data-testid="preview-hero-cta"
              className="group inline-flex items-center gap-2 bg-[#023CFA] hover:bg-[#1E52FF] text-white px-6 py-3.5 rounded-full text-[12px] tracking-[0.18em] uppercase font-semibold whitespace-nowrap"
              style={{
                ...FONT_MONO,
                transition: "background-color 300ms cubic-bezier(0.16, 1, 0.3, 1)",
                transform: "translateZ(0)",
                backfaceVisibility: "hidden",
                willChange: "background-color",
              }}
            >
              {t("pm.hero.cta")}
              <ArrowUpRight
                className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                style={{
                  transition: "transform 300ms cubic-bezier(0.16, 1, 0.3, 1)",
                  willChange: "transform",
                }}
                strokeWidth={2}
              />
            </button>
          </div>
          <div className="mt-10 flex items-center gap-6" style={FONT_BODY}>
            <div className="flex items-center gap-2 text-[15px]" style={{ color: "#E85D2C" }}>
              <BadgeCheck className="w-4 h-4" style={{ color: "#E85D2C" }} strokeWidth={2} />
              {t("pm.hero.badge.cam")}
            </div>
            <div className="flex items-center gap-2 text-[15px]" style={{ color: "#E85D2C" }}>
              <ShieldCheck className="w-4 h-4" style={{ color: "#E85D2C" }} strokeWidth={2} />
              {t("pm.hero.badge.fid")}
            </div>
          </div>
        </div>

        {/* Right: dashboard mock over skyline */}
        <div className="lg:col-span-5 relative">
          <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border" style={{ borderColor: BORDER }}>
            <img
              src="https://images.unsplash.com/photo-1506966953602-c20cc11f75e3?w=800&q=80"
              alt="Miami skyline"
              className="w-full h-full object-cover"
            />
          </div>
          {/* Reserve card — floats over image from 480px+, shrinks as needed, stacks below only on smallest screens */}
          <div
            className="mt-4 min-[480px]:mt-0 min-[480px]:absolute min-[480px]:-bottom-4 min-[480px]:-left-2 sm:-bottom-5 sm:-left-3 md:-bottom-8 md:-left-8 lg:-left-16 w-full min-[480px]:w-44 sm:w-52 md:w-64 lg:w-80 rounded-xl bg-white border p-2.5 min-[480px]:p-2.5 sm:p-3 md:p-3.5 lg:p-4"
            style={{ borderColor: BORDER, ...FONT_BODY, letterSpacing: "-0.01em" }}
          >
            <div className="flex items-center justify-between">
              <span className="text-[10px] min-[480px]:text-[10px] sm:text-[11px] md:text-[13px] font-semibold text-[#6E6E73]">
                {t("pm.hero.card.reserve")}
              </span>
              <span className="text-[10px] min-[480px]:text-[10px] sm:text-[11px] md:text-[13px] font-semibold" style={{ color: BLUE }}>
                {t("pm.hero.card.live")}
              </span>
            </div>
            <div
              className="mt-1 min-[480px]:mt-1 sm:mt-1.5 md:mt-2 text-[17px] min-[480px]:text-[17px] sm:text-[20px] md:text-2xl lg:text-3xl font-semibold text-[#0A0A0A]"
              style={{ ...FONT_HEAD, letterSpacing: "-0.02em" }}
            >
              $1,284,320
            </div>
            <div className="mt-1.5 min-[480px]:mt-1.5 sm:mt-2 md:mt-3 h-1 md:h-1.5 rounded-full bg-[#F3F4F6] overflow-hidden">
              <div className="h-full rounded-full" style={{ background: BLUE, width: "72%" }} />
            </div>
            <div className="mt-1 min-[480px]:mt-1 sm:mt-1.5 md:mt-2 text-[10px] min-[480px]:text-[10px] sm:text-[11px] md:text-[13px] font-normal text-[#6E6E73]">
              {t("pm.hero.card.target")}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------- Social proof strip -------------------- */
// Parse a stat string like "450+", "18%", "24/7" into a target integer + prefix/suffix
// so we can animate the numeric portion only.
function parseStat(raw) {
  const s = String(raw);
  // Match a leading run of digits (optionally with a decimal)
  const m = s.match(/^(\D*)(\d+)(.*)$/);
  if (!m) return { target: null, prefix: s, suffix: "" };
  return { target: parseInt(m[2], 10), prefix: m[1], suffix: m[3] };
}

function AnimatedStat({ raw, trigger }) {
  const { target, prefix, suffix } = parseStat(raw);
  const [value, setValue] = useState(target === null ? null : 0);
  const startedRef = useRef(false);

  useEffect(() => {
    if (target === null || !trigger || startedRef.current) return;
    startedRef.current = true;
    // Respect user's reduced-motion preference
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      setValue(target);
      return;
    }
    const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
    if (isMobile) {
      // No count-up animation on mobile — just show the final value.
      setValue(target);
      return;
    }
    const duration = 2200;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(target * eased));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [target, trigger]);

  return (
    <span>
      {target === null ? raw : `${prefix}${value}${suffix}`}
    </span>
  );
}

function ProofStrip() {
  const { t } = useT();
  const stats = [
    { n: t("pm.stat.doors.n"),   l: t("pm.stat.doors.l") },
    { n: t("pm.stat.cut.n"),     l: t("pm.stat.cut.l") },
    { n: t("pm.stat.years.n"),   l: t("pm.stat.years.l") },
    { n: t("pm.stat.support.n"), l: t("pm.stat.support.l") },
  ];
  const sectionRef = useRef(null);
  const [triggered, setTriggered] = useState(false);
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || triggered) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setTriggered(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [triggered]);
  return (
    <section ref={sectionRef} className="border-b" style={{ borderColor: BORDER }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4" style={{ borderColor: BORDER }}>
        {stats.map((s, i) => (
          <div
            key={i}
            className={
              "py-8 md:py-10 px-4 md:px-8 flex flex-col justify-center md:justify-start " +
              // Mobile: 2-col grid — flush left on odd children (1st, 3rd); left border on even (2nd, 4th); top border on bottom row (3rd, 4th)
              "[&:nth-child(2n+1)]:pl-0 [&:nth-child(2n)]:border-l " +
              "[&:nth-child(3)]:border-t [&:nth-child(4)]:border-t " +
              // md+: 4-col grid — restore left padding on odd, add left border on 2nd/3rd/4th but not 1st, remove top border
              "md:[&:nth-child(2n+1)]:pl-8 md:first:pl-0 " +
              "md:border-l md:first:border-l-0 md:[&:nth-child(2n)]:border-l " +
              "md:[&:nth-child(3)]:border-t-0 md:[&:nth-child(4)]:border-t-0"
            }
            style={{ borderColor: BORDER }}
          >
            <div className="text-3xl md:text-4xl font-semibold tracking-tight text-center" style={FONT_HEAD}>
              <AnimatedStat raw={s.n} trigger={triggered} />
            </div>
            <div className="mt-2 text-[11px] tracking-[0.18em] uppercase text-[#4B5563] text-center" style={FONT_MONO}>{s.l}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* -------------------- Services bento -------------------- */
function ServicesBento() {
  const { t } = useT();
  const svcs = [
    // First row — always visible (4)
    { icon: Calculator,    t: t("pm.svc.fin.t"),    d: t("pm.svc.fin.d") },
    { icon: Wrench,        t: t("pm.svc.vendor.t"), d: t("pm.svc.vendor.d") },
    { icon: Building2,     t: t("pm.svc.maint.t"),  d: t("pm.svc.maint.d") },
    { icon: MessageSquare, t: t("pm.svc.comm.t"),   d: t("pm.svc.comm.d") },
    // Second row — revealed on click (4)
    { icon: FileText,   t: t("pm.svc.admin.t"), d: t("pm.svc.admin.d") },
    { icon: Users,      t: t("pm.svc.board.t"), d: t("pm.svc.board.d") },
    { icon: Sparkles,   t: t("pm.svc.res.t"),   d: t("pm.svc.res.d") },
    { icon: ShieldCheck, t: t("pm.svc.gov.t"),  d: t("pm.svc.gov.d") },
  ];
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? svcs : svcs.slice(0, 4);

  return (
    <section id="services" className="border-b" style={{ borderColor: BORDER, background: "#F5F5F7" }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-16">
          <div className="lg:col-span-8">
            <h2 className="text-4xl md:text-5xl lg:text-6xl tracking-tight font-medium leading-[1.05]" style={FONT_HEAD}>
              {t("pm.svc.h1a")}<br />
              <span className="text-[#4B5563]">{t("pm.svc.h1b")}</span>
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 md:gap-3">
          {visible.map((s, i) => {
            const Icon = s.icon;
            const isNew = i >= 4;
            return (
              <div
                key={s.t}
                data-testid={`preview-service-${i}`}
                className="rounded-2xl md:rounded-3xl px-3 py-5 sm:px-4 sm:py-6 md:px-6 md:py-8 lg:px-8 lg:py-10 flex flex-col items-center text-center min-h-[190px] sm:min-h-[210px] md:min-h-[240px] lg:min-h-[280px]"
                style={{
                  background: "#FFFFFF",
                  transition: "padding 0.3s ease, min-height 0.3s ease, border-radius 0.3s ease",
                  animation: isNew ? "fadeInUp 0.35s ease both" : undefined,
                  animationDelay: isNew ? `${(i - 4) * 60}ms` : undefined,
                }}
              >
                <Icon
                  className="w-6 h-6 md:w-8 md:h-8 mb-3 md:mb-5"
                  style={{ color: BLUE, transition: "width 0.3s ease, height 0.3s ease, margin 0.3s ease" }}
                  strokeWidth={1.5}
                />
                {/* Title row — fixed min-height so 1-line and 2-line titles both occupy the same space,
                    guaranteeing the body text lines up horizontally across all cards. */}
                <h3
                  className="text-[14px] md:text-[19px] lg:text-[20px] font-semibold text-[#0A0A0A] flex items-start justify-center min-h-[2.6em] md:min-h-[2.5em]"
                  style={{ ...FONT_HEAD, letterSpacing: "-0.01em", lineHeight: 1.2, transition: "font-size 0.3s ease" }}
                >
                  {s.t}
                </h3>
                <p
                  className="mt-2 md:mt-3 text-[12px] md:text-[14px] leading-[1.45] md:leading-[1.5] text-[#515154] max-w-[26ch]"
                  style={{ ...FONT_BODY, transition: "font-size 0.3s ease, margin 0.3s ease" }}
                >
                  {s.d}
                </p>
              </div>
            );
          })}
        </div>

        <div className="mt-12 flex justify-center">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            data-testid="preview-services-toggle"
            aria-expanded={expanded}
            className="services-toggle inline-flex items-center rounded-full px-4 py-1.5 text-[14px] font-normal"
            style={{
              ...FONT_BODY,
              color: BLUE,
              border: `1px solid ${BLUE}`,
              background: "transparent",
              letterSpacing: "-0.01em",
              transition: "background-color 0.2s ease, color 0.2s ease",
            }}
          >
            {expanded ? t("pm.svc.showLess") : t("pm.svc.viewAll")}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .services-toggle:hover { background-color: #023CFA !important; color: #FFFFFF !important; }
      `}</style>
    </section>
  );
}

/* -------------------- Technology / Portal -------------------- */
// Apple Continuity-style tab nav with left/right chevron scroll controls
function TabsNav({ features, active, setActive }) {
  const scrollerRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const update = () => {
      const overflow = el.scrollWidth - el.clientWidth;
      setCanLeft(el.scrollLeft > 2);
      setCanRight(el.scrollLeft < overflow - 2);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, [features.length]);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(el.clientWidth * 0.7, 200), behavior: "smooth" });
  };

  // When the active tab changes (or on first render), make sure it is fully
  // visible. Last tab → snap to the right end (full text visible, left side
  // clipped with a left-arrow indicator). First tab → snap to the start.
  // Middle tabs → nudge just enough to keep them fully in view.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const overflow = el.scrollWidth - el.clientWidth;
    if (overflow <= 0) return; // nothing to scroll — no overflow
    const btn = el.querySelector(`[data-testid="tech-tab-${active}"]`);
    if (!btn) return;
    if (active === features.length - 1) {
      el.scrollTo({ left: overflow, behavior: "smooth" });
    } else if (active === 0) {
      el.scrollTo({ left: 0, behavior: "smooth" });
    } else {
      const btnRect = btn.getBoundingClientRect();
      const cRect = el.getBoundingClientRect();
      const overflowRight = btnRect.right - cRect.right;
      const overflowLeft = cRect.left - btnRect.left;
      if (overflowRight > 0) el.scrollBy({ left: overflowRight + 24, behavior: "smooth" });
      else if (overflowLeft > 0) el.scrollBy({ left: -(overflowLeft + 24), behavior: "smooth" });
    }
  }, [active, features.length]);

  return (
    <div className="mt-10 relative">
      <div className="border-b" style={{ borderColor: "rgba(255,255,255,0.14)" }}>
        <div
          ref={scrollerRef}
          className="flex flex-nowrap items-end justify-start md:justify-center gap-8 md:gap-10 overflow-x-auto overflow-y-hidden no-scrollbar h-10"
          style={{ WebkitOverflowScrolling: "touch", overscrollBehaviorX: "contain", overscrollBehaviorY: "none", touchAction: "pan-x", justifyContent: "safe center" }}
        >
          {features.map((f, i) => {
            const isActive = i === active;
            return (
              <button
                key={f.label}
                onClick={() => setActive(i)}
                data-testid={`tech-tab-${i}`}
                aria-selected={isActive}
                className="relative pb-3 shrink-0 transition-colors leading-none"
                style={{ ...FONT_BODY, letterSpacing: "-0.01em" }}
              >
                <span
                  className={
                    "text-[15px] whitespace-nowrap leading-none " +
                    (isActive
                      ? "text-white font-semibold"
                      : "text-white/50 hover:text-white/80 font-normal")
                  }
                >
                  {f.label}
                </span>
                {isActive && (
                  <span
                    aria-hidden
                    className="absolute left-0 right-0 -bottom-px h-[2px]"
                    style={{ background: "#FFFFFF", borderRadius: 2 }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Left chevron — appears when scrolled right of start */}
      {canLeft && (
        <>
          <div
            aria-hidden
            className="absolute left-0 top-0 bottom-1 w-12 pointer-events-none"
            style={{
              background:
                "linear-gradient(90deg, #000 30%, rgba(0,0,0,0) 100%)",
            }}
          />
          <button
            type="button"
            onClick={() => scrollBy(-1)}
            aria-label="Scroll tabs left"
            data-testid="tabs-scroll-left"
            className="absolute left-0 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full text-white/60 hover:text-white transition-colors"
          >
            <ChevronLeft className="w-5 h-5" strokeWidth={1.75} />
          </button>
        </>
      )}

      {/* Right chevron — appears when there's more to scroll */}
      {canRight && (
        <>
          <div
            aria-hidden
            className="absolute right-0 top-0 bottom-1 w-12 pointer-events-none"
            style={{
              background:
                "linear-gradient(270deg, #000 30%, rgba(0,0,0,0) 100%)",
            }}
          />
          <button
            type="button"
            onClick={() => scrollBy(1)}
            aria-label="Scroll tabs right"
            data-testid="tabs-scroll-right"
            className="absolute right-0 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full text-white/60 hover:text-white transition-colors"
          >
            <ChevronRight className="w-5 h-5" strokeWidth={1.75} />
          </button>
        </>
      )}
    </div>
  );
}

function TechnologyPortal() {
  const { t } = useT();
  const features = [
    { label: t("pm.tech.pill.fin"),       caption: t("pm.tech.cap.fin") },
    { label: t("pm.tech.pill.approvals"), caption: t("pm.tech.cap.approvals") },
    { label: t("pm.tech.pill.bids"),      caption: t("pm.tech.cap.bids") },
    { label: t("pm.tech.pill.payments"),  caption: t("pm.tech.cap.payments") },
    { label: t("pm.tech.pill.orders"),    caption: t("pm.tech.cap.orders") },
    { label: t("pm.tech.pill.reserve"),   caption: t("pm.tech.cap.reserve") },
  ];
  const [active, setActive] = useState(0);

  // Desktop-only: slide devices in from off-screen when the frame enters view.
  // Animation plays ONLY when the user is scrolling DOWN into the frame.
  // If the user scrolls past the section and then scrolls back UP into it,
  // the devices simply snap into place — no re-animation.
  const [isDesktop, setIsDesktop] = useState(false);
  const [entered, setEntered] = useState(false);
  const [animate, setAnimate] = useState(false); // whether the CSS transition should apply
  const frameRef = useRef(null);
  const dirRef = useRef("down");
  const lastYRef = useRef(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(min-width: 768px)");
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (!isDesktop) return;
    lastYRef.current = window.scrollY || window.pageYOffset || 0;
    const onScroll = () => {
      const y = window.scrollY || window.pageYOffset || 0;
      dirRef.current = y > lastYRef.current ? "down" : "up";
      lastYRef.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isDesktop]);

  useEffect(() => {
    if (!isDesktop) return;
    const el = frameRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            if (dirRef.current === "down") {
              setAnimate(true);
              setEntered(true);
            } else {
              // Re-entering via up-scroll: skip the entrance animation.
              setAnimate(false);
              setEntered(true);
            }
          } else {
            // Fully out of view — reset for the next down-scroll entry.
            setEntered(false);
          }
        });
      },
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [isDesktop]);

  const ease = "cubic-bezier(0.16, 1, 0.3, 1)";
  const duration = 2400;
  const shouldTween = isDesktop && entered && animate;
  const monitorStyle = isDesktop
    ? {
        transform: entered ? "translateX(0)" : "translateX(-120vw)",
        opacity: entered ? 1 : 0,
        transition: shouldTween
          ? `transform ${duration}ms ${ease}, opacity ${duration * 0.6}ms ease`
          : "none",
        willChange: "transform, opacity",
      }
    : undefined;
  const phoneStyle = isDesktop
    ? {
        transform: entered ? "translateX(0)" : "translateX(120vw)",
        opacity: entered ? 1 : 0,
        transition: shouldTween
          ? `transform ${duration}ms ${ease}, opacity ${duration * 0.6}ms ease`
          : "none",
        willChange: "transform, opacity",
      }
    : undefined;

  return (
    <section
      id="technology"
      data-testid="technology-section"
      className="border-b"
      style={{ borderColor: "#1A1A1A", background: "#000000", color: "#FFFFFF" }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="max-w-2xl">
          <h2
            className="text-4xl md:text-5xl lg:text-6xl tracking-tight font-medium leading-[1.05] text-white"
            style={FONT_HEAD}
          >
            {t("pm.tech.h1a")}<br />
            <span className="text-white/45">{t("pm.tech.h1b")}</span>
          </h2>
          <p className="mt-6 max-w-lg text-[15px] leading-[1.55] text-white/60" style={FONT_BODY}>
            {t("pm.tech.sub")}
          </p>
        </div>

        {/* Device image inside its own dark-grey rounded card — monitor + phone rendered as separate images so we can space them apart and vertically center the phone */}
        <div className="mt-12">
          <div
            ref={frameRef}
            className="rounded-3xl overflow-hidden px-6 py-20 md:py-24 flex items-center justify-center"
            style={{ background: "#1D1D1F" }}
          >
            <div className="w-full max-w-2xl mx-auto flex items-center justify-center gap-3 md:gap-10 lg:gap-14">
              <img
                src="/images/tech-monitor.png"
                alt="PropHero community portal dashboard"
                className="block h-auto w-[72%] md:w-[62%]"
                style={monitorStyle}
              />
              <img
                src="/images/tech-phone.png"
                alt="PropHero mobile app"
                className="block h-auto self-center w-[19%] md:w-[15%]"
                style={phoneStyle}
              />
            </div>
          </div>
        </div>

        {/* Apple Continuity-style tab nav with left/right chevron controls */}
        <TabsNav features={features} active={active} setActive={setActive} />

        {/* Active tab caption — white, centered */}
        <p
          className="mt-6 max-w-2xl mx-auto text-center text-[15px] leading-[1.45] text-white"
          style={{ ...FONT_BODY, letterSpacing: "-0.01em" }}
        >
          {features[active].caption}
        </p>
      </div>
      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { scrollbar-width: none; }
      `}</style>
    </section>
  );
}

/* -------------------- Comparison -------------------- */
function Comparison() {
  const { t } = useT();
  const rows = [
    [t("pm.cmp.row.access"), true, false],
    [t("pm.cmp.row.bids"),   true, false],
    [t("pm.cmp.row.fin"),    true, false],
    [t("pm.cmp.row.comm"),   true, false],
    [t("pm.cmp.row.portal"), true, false],
    [t("pm.cmp.row.fees"),   false, true],
  ];
  return (
    <section id="compare" className="border-b" style={{ borderColor: BORDER }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="max-w-2xl mb-14">
          <h2 className="text-4xl md:text-5xl lg:text-6xl tracking-tight font-medium leading-[1.05]" style={FONT_HEAD}>
            {t("pm.cmp.h1")}
          </h2>
        </div>

        <div className="border rounded-2xl overflow-hidden" style={{ borderColor: BORDER }}>
          <div className="grid grid-cols-12 items-center border-b py-4 px-6" style={{ borderColor: BORDER, background: SURFACE, ...FONT_MONO }}>
            <div className="col-span-6 text-[10px] tracking-[0.1em] md:tracking-[0.22em] uppercase text-[#4B5563] whitespace-nowrap">{t("pm.cmp.col.cap")}</div>
            <div className="col-span-3 text-[10px] tracking-[0.1em] md:tracking-[0.22em] uppercase whitespace-nowrap text-center" style={{ color: BLUE }}>{t("pm.cmp.col.ph")}</div>
            <div className="col-span-3 text-[10px] tracking-[0.1em] md:tracking-[0.22em] uppercase text-[#4B5563] whitespace-nowrap text-center">{t("pm.cmp.col.nat")}</div>
          </div>
          {rows.map(([label, ph, natl], i) => (
            <div
              key={i}
              className="grid grid-cols-12 py-4 px-6 items-center border-b last:border-0"
              style={{ borderColor: BORDER }}
            >
              <div className="col-span-6 text-[14px] text-[#0A0A0A]">{label}</div>
              <div className="col-span-3 flex justify-center">
                {ph ? (
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full" style={{ background: `${BLUE}12` }}>
                    <Check className="w-4 h-4" style={{ color: BLUE }} strokeWidth={2.5} />
                  </span>
                ) : (
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#F3F4F6]">
                    <X className="w-4 h-4 text-[#9CA3AF]" strokeWidth={2.5} />
                  </span>
                )}
              </div>
              <div className="col-span-3 flex justify-center">
                {natl ? (
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full" style={{ background: `${BLUE}12` }}>
                    <Check className="w-4 h-4" style={{ color: BLUE }} strokeWidth={2.5} />
                  </span>
                ) : (
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#F3F4F6]">
                    <X className="w-4 h-4 text-[#9CA3AF]" strokeWidth={2.5} />
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------- Onboarding — Option A (Vertical Timeline, TIGHT) -------------------- */
function ProcessScrollStage({ steps }) {
  const stageRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [subProgress, setSubProgress] = useState(0);

  // Each step is allocated a portion of the stage's total scroll distance.
  // First and last get a SHORTER slice so the sticky container releases
  // quickly at both ends → smooth handoff into the sections above and below.
  const weights = useMemo(() => {
    const arr = steps.map(() => 1);
    if (arr.length > 0) arr[0] = 0.5;
    if (arr.length > 1) arr[arr.length - 1] = 0.35;
    return arr;
  }, [steps]);
  const totalWeight = useMemo(() => weights.reduce((a, b) => a + b, 0), [weights]);
  const cumWeights = useMemo(() => {
    const c = [];
    let acc = 0;
    weights.forEach((w) => { acc += w; c.push(acc); });
    return c;
  }, [weights]);

  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = stageRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const total = rect.height - window.innerHeight;
        const scrolled = Math.max(0, Math.min(-rect.top, total));
        const overall = total > 0 ? scrolled / total : 0;
        const target = overall * totalWeight;
        let idx = 0;
        for (let i = 0; i < weights.length; i++) {
          if (target <= cumWeights[i]) { idx = i; break; }
          idx = i;
        }
        const prevCum = idx > 0 ? cumWeights[idx - 1] : 0;
        const w = weights[idx] || 1;
        const sub = Math.max(0, Math.min(1, (target - prevCum) / w));
        setActiveIdx(idx);
        setSubProgress(sub);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, [totalWeight, weights, cumWeights]);

  // Text visibility phases:
  //   0.00 → 0.15 : fade in (light → dark)
  //   0.15 → 0.75 : hold fully dark
  //   0.75 → 1.00 : fade out (dark → light)
  // Step 0 → skip fade-in.
  // Last step → skip fade-out (stays fully visible into the next section).
  let colorT;
  const isLast = activeIdx === steps.length - 1;
  if (isLast) {
    colorT = activeIdx === 0 ? 1 : (subProgress < 0.15 ? subProgress / 0.15 : 1);
  } else if (activeIdx === 0) {
    colorT = subProgress < 0.75 ? 1 : Math.max(0, 1 - (subProgress - 0.75) / 0.25);
  } else if (subProgress < 0.15) {
    colorT = subProgress / 0.15;
  } else if (subProgress > 0.75) {
    colorT = Math.max(0, 1 - (subProgress - 0.75) / 0.25);
  } else {
    colorT = 1;
  }

  const lerp = (a, b, t) => Math.round(a + (b - a) * t);
  const lerpHex = (from, to, t) => {
    const fh = from.slice(1), th = to.slice(1);
    const fr = parseInt(fh.slice(0, 2), 16), fg = parseInt(fh.slice(2, 4), 16), fb = parseInt(fh.slice(4, 6), 16);
    const tr = parseInt(th.slice(0, 2), 16), tg = parseInt(th.slice(2, 4), 16), tb = parseInt(th.slice(4, 6), 16);
    return `rgb(${lerp(fr, tr, t)}, ${lerp(fg, tg, t)}, ${lerp(fb, tb, t)})`;
  };
  const LIGHT = "#D6D9DE";
  const bodyColor = lerpHex(LIGHT, "#0A0A0A", colorT);
  const headColor = lerpHex(LIGHT, "#000000", colorT);

  return (
    <div ref={stageRef} style={{ height: `${totalWeight * 100 + 100}vh` }} className="relative">
      <div className="sticky top-0 h-screen flex flex-col items-center justify-center px-6 lg:px-10">
        {/* Chips row */}
        <div className="flex gap-4 mb-5" data-testid="process-chips">
          {steps.map((s, i) => {
            const isActive = i === activeIdx;
            return (
              <div
                key={s.n}
                data-testid={`process-chip-${i}`}
                aria-current={isActive}
                className="rounded-2xl flex flex-col items-center justify-center px-2"
                style={{
                  width: 68,
                  height: 68,
                  background: isActive
                    ? `radial-gradient(120% 90% at 20% 15%, #6B8FFF 0%, #3A6BFF 35%, #023CFA 75%, #001FB0 100%)`
                    : "#FFFFFF",
                  color: isActive ? "#FFFFFF" : "#0A0A0A",
                  border: isActive ? "1px solid rgba(2,60,250,0.4)" : "1px solid #E5E7EB",
                  boxShadow: isActive
                    ? "0 10px 28px rgba(2, 60, 250, 0.28), inset 0 1px 0 rgba(255,255,255,0.25)"
                    : "0 1px 2px rgba(0,0,0,0.04)",
                  transition:
                    "background 600ms cubic-bezier(0.22, 1, 0.36, 1), color 400ms ease, box-shadow 500ms ease, border-color 400ms ease",
                }}
              >
                <span className="text-[9px] tracking-[0.22em] uppercase" style={{ opacity: 0.75, fontWeight: 600 }}>{s.n}</span>
                <span className="text-[12px] mt-1.5 font-semibold leading-none">{s.chip}</span>
              </div>
            );
          })}
        </div>

        {/* Description */}
        <div className="text-center max-w-xl">
          <h3
            className="text-3xl md:text-4xl font-semibold"
            style={{
              ...FONT_HEAD,
              letterSpacing: "-0.02em",
              color: headColor,
              transition: "color 700ms cubic-bezier(0.22, 1, 0.36, 1)",
            }}
          >
            {steps[activeIdx].t}
          </h3>
          <p
            className="mt-5 text-[18px] md:text-[20px] leading-relaxed"
            style={{
              color: bodyColor,
              transition: "color 700ms cubic-bezier(0.22, 1, 0.36, 1)",
              minHeight: "6.5em",
            }}
          >
            {steps[activeIdx].d}
          </p>
        </div>
      </div>
    </div>
  );
}

function OnboardingStepperOptionA() {
  const { t } = useT();
  const steps = [
    { n: "01", t: t("pm.proc.s1.t"), chip: t("pm.proc.s1.chip"), d: t("pm.proc.s1.d") },
    { n: "02", t: t("pm.proc.s2.t"), chip: t("pm.proc.s2.chip"), d: t("pm.proc.s2.d") },
    { n: "03", t: t("pm.proc.s3.t"), chip: t("pm.proc.s3.chip"), d: t("pm.proc.s3.d") },
    { n: "04", t: t("pm.proc.s4.t"), chip: t("pm.proc.s4.chip"), d: t("pm.proc.s4.d") },
    { n: "05", t: t("pm.proc.s5.t"), chip: t("pm.proc.s5.chip"), d: t("pm.proc.s5.d") },
  ];
  return (
    <section id="process" className="border-b" style={{ borderColor: BORDER, background: SURFACE }}>
      {/* Heading — same placement */}
      <div className="max-w-5xl mx-auto px-6 lg:px-10 pt-24 md:pt-16 lg:pt-20 pb-6 md:pb-0">
        <div className="max-w-2xl">
          <h2 className="text-4xl md:text-5xl lg:text-6xl tracking-tight font-medium leading-[1.05]" style={FONT_HEAD}>
            {t("pm.proc.h1a")}{" "}
            <span className="text-[#4B5563]">{t("pm.proc.h1b")}</span>
          </h2>
        </div>
      </div>

      {/* Mobile: vertical timeline with small numbered chip boxes + connector line */}
      <MobileProcessTimeline steps={steps} />
      {/* Desktop: Apple-style scroll-driven stage */}
      <div className="hidden md:block">
        <ProcessScrollStage steps={steps} />
      </div>
    </section>
  );
}

function MobileProcessTimeline({ steps }) {
  const [activeIdx, setActiveIdx] = useState(-1); // -1 = nothing active yet
  const itemsRef = useRef([]);
  const containerRef = useRef(null);
  const lastYRef = useRef(0);
  const highestReachedRef = useRef(-1);
  // Locked = boxes are empty and cannot re-fill on further scrolling until
  // the user scrolls UP above the entire section (section fully below viewport)
  // and then back DOWN through it again.
  const lockedRef = useRef(false);

  useEffect(() => {
    let raf = 0;
    lastYRef.current = window.scrollY || 0;

    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const y = window.scrollY || 0;
        const dir = y > lastYRef.current ? "down" : y < lastYRef.current ? "up" : "same";
        lastYRef.current = y;

        const c = containerRef.current;
        // Rearm ONLY when the section is fully below the viewport — i.e. user
        // has scrolled UP past the top of the section. Continued down-scroll
        // must never rearm; the lock must persist until they come back up
        // above the section.
        if (c) {
          const cRect = c.getBoundingClientRect();
          if (cRect.top > window.innerHeight) {
            lockedRef.current = false;
            if (highestReachedRef.current !== -1) {
              highestReachedRef.current = -1;
              setActiveIdx(-1);
            }
            return;
          }
        }

        // Lock (and clear boxes) once the last item scrolls above the
        // viewport while going DOWN. Up-scroll never triggers a lock.
        const lastItem = itemsRef.current[itemsRef.current.length - 1];
        if (lastItem && dir === "down") {
          const lastRect = lastItem.getBoundingClientRect();
          if (lastRect.bottom < 0) {
            lockedRef.current = true;
            if (highestReachedRef.current !== -1) {
              highestReachedRef.current = -1;
              setActiveIdx(-1);
            }
            return;
          }
        }

        // Only advance on down-scroll; ignore up-scroll entirely.
        if (dir !== "down") return;
        // Locked → don't reactivate until section has been fully scrolled above.
        if (lockedRef.current) return;

        // Trigger point — the higher the item reaches, the sooner it activates.
        const triggerY = window.innerHeight * 0.7;
        let reached = highestReachedRef.current;
        for (let i = 0; i < itemsRef.current.length; i++) {
          const el = itemsRef.current[i];
          if (!el) continue;
          const rect = el.getBoundingClientRect();
          const itemCenter = rect.top + rect.height / 2;
          if (itemCenter <= triggerY) {
            if (i > reached) reached = i;
          }
        }
        if (reached !== highestReachedRef.current) {
          highestReachedRef.current = reached;
          setActiveIdx(reached);
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div ref={containerRef} className="md:hidden max-w-5xl mx-auto px-6 pb-24">
      <ol className="space-y-5">
        {steps.map((s, i) => {
          const isActive = activeIdx >= 0 && i <= activeIdx;
          return (
            <li
              key={s.n}
              ref={(el) => (itemsRef.current[i] = el)}
              className="flex items-start gap-4"
            >
              <div
                aria-hidden
                className="flex-shrink-0 rounded-xl flex items-center justify-center"
                style={{
                  width: 36,
                  height: 36,
                  background: isActive ? "#4B5563" : "#FFFFFF",
                  border: isActive ? "1px solid #4B5563" : "1px solid #E5E7EB",
                  color: isActive ? "#FFFFFF" : "#0A0A0A",
                  boxShadow: isActive ? "none" : "0 1px 2px rgba(0,0,0,0.04)",
                  transition:
                    "background 220ms ease-out, color 180ms ease-out, box-shadow 220ms ease-out, border-color 180ms ease-out",
                }}
              >
                <span className="text-[10px] tracking-[0.15em] font-semibold">{s.n}</span>
              </div>
              <div className="pt-0.5 flex-1">
                <h3 className="text-lg font-medium tracking-tight leading-tight" style={FONT_HEAD}>
                  {s.t}
                </h3>
                <p className="mt-1 text-[14px] leading-relaxed text-[#4B5563]">
                  {s.d}
                </p>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function OnboardingStepper() {
  return <OnboardingStepperOptionA />;
}

/* -------------------- Final CTA -------------------- */
function FinalCta({ openBooking }) {
  const { t } = useT();
  return (
    <section className="bg-[#023CFA]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <h2 className="text-4xl md:text-5xl lg:text-6xl tracking-tight font-medium leading-[1.05] text-white" style={FONT_HEAD}>
              {t("pm.final.h1a")}<br />{t("pm.final.h1b")}
            </h2>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <button
              onClick={() => openBooking("preview_final_cta", "Schedule Consultation")}
              data-testid="preview-final-cta"
              className="group inline-flex items-center gap-2 bg-white text-[#0A0A0A] hover:bg-[#EEF2FF] px-6 py-3.5 rounded-full text-[12px] tracking-[0.18em] uppercase font-semibold whitespace-nowrap"
              style={{
                ...FONT_MONO,
                transition: "background-color 300ms cubic-bezier(0.16, 1, 0.3, 1)",
                transform: "translateZ(0)",
                backfaceVisibility: "hidden",
                willChange: "background-color",
              }}
            >
              {t("pm.final.cta")}
              <ArrowRight
                className="w-4 h-4 group-hover:translate-x-1"
                style={{
                  transition: "transform 300ms cubic-bezier(0.16, 1, 0.3, 1)",
                  willChange: "transform",
                }}
                strokeWidth={2}
              />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
